import { useAuth } from '@/app/context';
import { type MenuItem } from '@/shared/constants';
import { useRequirePermissions } from '@/shared/hooks';
import { useRouterState } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { isMenuPathActive } from './menu-active.util';
import { SidebarSubMenuItem } from './sidebar-sub-menu-item.component';

interface SidebarMenuGroupProps {
  item: MenuItem;
  isExpanded: boolean;
  onMenuClick: () => void;
}

export function SidebarMenuGroup({
  item,
  isExpanded,
  onMenuClick,
}: SidebarMenuGroupProps) {
  const subItems = item.subItems ?? [];
  const { groups: userGroups } = useAuth();

  // União das permissões/grupos dos filhos: o grupo só aparece se o usuário
  // tiver acesso a pelo menos um submenu.
  const unionPermissions = useMemo(
    () => Array.from(new Set(subItems.flatMap(s => s.permissions ?? []))),
    [subItems]
  );
  const unionGroups = useMemo(
    () => Array.from(new Set(subItems.flatMap(s => s.groups ?? []))),
    [subItems]
  );

  const { hasPermission, isChecking } = useRequirePermissions({
    permissions: unionPermissions,
  });

  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const hasActiveChild = subItems.some(s =>
    isMenuPathActive(currentPath, s.id)
  );

  const [isOpen, setIsOpen] = useState(true);

  const passesGroupGate =
    unionGroups.length > 0 && !!userGroups?.some(g => unionGroups.includes(g));
  const passesPermissionGate =
    unionPermissions.length > 0 ? hasPermission === true : false;
  const stillChecking = unionPermissions.length > 0 && isChecking;

  // Enquanto checa permissão (e ainda não passou pelo gate de grupo), não pisca.
  if (stillChecking && !passesGroupGate) {
    return null;
  }

  if (!passesGroupGate && !passesPermissionGate) {
    return null;
  }

  const Icon = item.icon;
  const showSubItems = isExpanded && isOpen;
  // Quando os filhos não estão visíveis, o pai assume o destaque do filho ativo.
  const highlightParent = hasActiveChild && !showSubItems;

  return (
    <div className='flex flex-col'>
      <button
        type='button'
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        data-active={highlightParent ? 'true' : undefined}
        className={`
          relative flex h-10 w-full items-center gap-3 rounded-md transition-all duration-300 ease-in-out
          ${isExpanded ? 'px-3' : 'px-2.5'}
          ${
            highlightParent
              ? 'bg-alba-green-500 text-white'
              : 'text-white hover:bg-alba-blue-700'
          }
        `}
      >
        <Icon className='h-5 w-5 flex-shrink-0' strokeWidth={1.6} />
        <span
          className={`flex-1 overflow-hidden whitespace-nowrap text-left text-sm font-medium transition-opacity duration-300 ${
            isExpanded ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {item.label}
        </span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 transition-all duration-300 ${
            isExpanded ? 'opacity-100' : 'opacity-0'
          } ${isOpen ? 'rotate-180' : ''}`}
          strokeWidth={1.6}
        />
      </button>

      {showSubItems && (
        <div className='ml-3 mt-1 flex flex-col gap-1 border-l border-alba-blue-700/50 pl-3'>
          {subItems.map(sub => (
            <SidebarSubMenuItem
              key={sub.id}
              item={sub}
              onMenuClick={onMenuClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
